import type { TerminalRecordingSession } from "@p0security/shared/integrations/sessions/types";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useEffect, useMemo, useRef } from "react";

import { findCutoffIndex, inferTerminalDimensions } from "./terminalPlayback";

const XTERM_THEME = {
  background: "#111",
  foreground: "#f5f5f5",
};

const DEFAULT_COLS = 80;
const DEFAULT_ROWS = 24;
// Approximate width of a single monospace character in xterm.js's default font.
const CHAR_WIDTH_PX = 9.5;
// Horizontal padding added by xterm.js around the terminal viewport.
const TERMINAL_PADDING_PX = 32;

const RESIZE_PATTERN = /(\d+)x(\d+)/;

const WHEEL_LINE_HEIGHT_PX = 16;

const getWheelDeltaPixels = (
  delta: number,
  deltaMode: number,
  pageSize: number
): number => {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return delta * WHEEL_LINE_HEIGHT_PX;
  }

  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return delta * pageSize;
  }

  return delta;
};

const newTerminal = (
  container: HTMLDivElement,
  scrollContainer: HTMLDivElement,
  cols: number,
  rows: number
): Terminal => {
  container.textContent = "";

  const term = new Terminal({
    cols,
    rows,
    theme: XTERM_THEME,
    scrollback: 10000,
    convertEol: true,
    disableStdin: true,
  });

  term.open(container);

  // Hide scrollbar to prevent it from stealing a column.
  const viewport = container.querySelector<HTMLElement>(".xterm-viewport");
  if (viewport) viewport.style.overflow = "hidden";

  term.attachCustomWheelEventHandler((event) => {
    const verticalDelta = getWheelDeltaPixels(
      event.deltaY,
      event.deltaMode,
      scrollContainer.clientHeight || 0
    );
    const horizontalDelta = getWheelDeltaPixels(
      event.deltaX,
      event.deltaMode,
      scrollContainer.clientWidth || 0
    );

    if (horizontalDelta !== 0) {
      scrollContainer.scrollLeft += horizontalDelta;
    }

    if (verticalDelta !== 0) {
      scrollContainer.scrollTop += verticalDelta;
    }

    if (horizontalDelta !== 0 || verticalDelta !== 0) {
      event.preventDefault();
      return false;
    }

    return true;
  });

  return term;
};

const writeToTerminal = (term: Terminal, data: string) => {
  return new Promise<void>((resolve) => {
    term.write(data, resolve);
  });
};

const writeEventsToTerminal = async (
  term: Terminal,
  session: TerminalRecordingSession,
  fromIndex: number,
  toIndex: number,
  shouldAbort: () => boolean
) => {
  const { events } = session;
  let pendingOutput = "";

  const flushPendingOutput = async () => {
    if (!pendingOutput || shouldAbort()) {
      pendingOutput = "";
      return;
    }

    const output = pendingOutput;
    pendingOutput = "";
    await writeToTerminal(term, output);
  };

  for (let i = fromIndex; i < toIndex; i++) {
    if (shouldAbort()) {
      return;
    }

    const event = events[i]!;
    if (event.type === "m") continue;
    if (event.type === "i") continue;
    if (event.type === "r") {
      await flushPendingOutput();
      if (shouldAbort()) {
        return;
      }

      const match = event.data.match(RESIZE_PATTERN);
      if (match) {
        const resizeCols = Number(match[1]);
        const resizeRows = Number(match[2]);
        if (resizeCols > 0 && resizeRows > 0) {
          term.resize(resizeCols, resizeRows);
        }
      }
      continue;
    }

    if (event.type !== "o") continue;
    pendingOutput += event.data;
  }

  await flushPendingOutput();
};

export const XtermPlayer = ({
  session,
  playheadSeconds,
  maxHeight,
  showScrollbars = false,
}: {
  session: TerminalRecordingSession;
  playheadSeconds: number;
  maxHeight?: number | string;
  showScrollbars?: boolean;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const lastRenderedIndexRef = useRef(0);
  const renderQueueRef = useRef(Promise.resolve());
  const dimensions = useMemo(() => inferTerminalDimensions(session), [session]);
  const cols = dimensions.cols ?? DEFAULT_COLS;
  const rows = dimensions.rows ?? DEFAULT_ROWS;
  const estimatedTerminalWidthPx = Math.round(
    cols * CHAR_WIDTH_PX + TERMINAL_PADDING_PX
  );

  // Initialize terminal on mount and when session changes
  useEffect(() => {
    const container = containerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!container || !scrollContainer) return;

    const term = newTerminal(container, scrollContainer, cols, rows);
    termRef.current = term;
    lastRenderedIndexRef.current = 0;
    renderQueueRef.current = Promise.resolve();

    return () => {
      term.dispose();
      container.textContent = "";
      termRef.current = null;
      renderQueueRef.current = Promise.resolve();
    };
  }, [session.sessionId, cols, rows]);

  // Write events based on playhead position
  useEffect(() => {
    const targetIndex = findCutoffIndex(session.events, playheadSeconds);
    if (targetIndex === lastRenderedIndexRef.current) return;

    renderQueueRef.current = renderQueueRef.current
      .then(async () => {
        const container = containerRef.current;
        let term = termRef.current;
        if (!container || !term) return;

        const lastIndex = lastRenderedIndexRef.current;
        if (targetIndex === lastIndex) return;
        let fromIndex = lastIndex;

        if (targetIndex < lastIndex) {
          // Recreate the terminal so xterm's buffered writes and resized geometry
          // cannot leak into the rewound playback state.
          term.dispose();
          const scrollContainer = scrollContainerRef.current;
          if (!scrollContainer) return;
          term = newTerminal(container, scrollContainer, cols, rows);
          termRef.current = term;
          lastRenderedIndexRef.current = 0;
          fromIndex = 0;
        }

        if (targetIndex > fromIndex) {
          // xterm.write() is buffered, but resize() is synchronous. Flush output
          // before applying a resize so fullscreen apps like vim keep the
          // recording's original ordering.
          await writeEventsToTerminal(
            term,
            session,
            fromIndex,
            targetIndex,
            () => {
              return termRef.current !== term;
            }
          );
        }

        if (termRef.current === term) {
          lastRenderedIndexRef.current = targetIndex;
        }
      })
      .catch(() => {
        // Reset the queue so subsequent writes are not blocked by a failed
        // render step.
        renderQueueRef.current = Promise.resolve();
      });
  }, [playheadSeconds, session, cols, rows]);

  return (
    <div
      className={`terminal-replay-scroll-container${
        showScrollbars ? " terminal-replay-scroll-container-visible" : ""
      }`}
      ref={scrollContainerRef}
      style={{
        minHeight: "160px",
        maxHeight,
        overflowX: "auto",
        overflowY: maxHeight ? "auto" : "hidden",
        overscrollBehavior: "contain",
      }}
    >
      <div
        ref={containerRef}
        style={{
          minWidth: `${estimatedTerminalWidthPx}px`,
          width: "max-content",
        }}
      />
    </div>
  );
};
